import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../components/common/spinner';
import css from './create-or-edit-modal.module.css';

interface CreateFolderModalProps {
  onClose: (type?: string) => void;
  value: any;
  items?: any[];
  editItem?: any;
  handleOnSubmit?: any;
  title: string;
  label?: string;
  submitTitle: string;
}

const CreateOrEditModal: React.FC<CreateFolderModalProps> = ({
  onClose,
  value,
  items,
  editItem,
  handleOnSubmit,
  title,
  label,
  submitTitle,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      name: value?.name || '',
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = useCallback(
    async (data: { name: string }) => {
      const isDuplicate = items.some((item) => item.name === data.name);

      if (isDuplicate) {
        setError(t('pages.document_storage.duplicate_folder'));
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        await handleOnSubmit(data);
        reset();
      } catch (err) {
        console.error('Error creating/editing folder:', err);
        setError(t('pages.document_storage.failied_to_crete_edit'));
      } finally {
        setIsLoading(false);
      }
    },
    [items, reset, handleOnSubmit, t]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={css['container']}>
      <h1>{title}</h1>
      <label htmlFor="folder-name">{label}</label>
      <input
        type="text"
        id="folder-name"
        autoFocus
        {...register('name', { required: true, maxLength: 80 })}
      />
      {error && <p className="text-error">{error}</p>}
      <div className={css['button-container']}>
        <button
          type="submit"
          disabled={isLoading}
          className={isLoading ? css['loading'] : ''}
        >
          <Spinner visible={isLoading} size="sm" className="mr-2" />
          {submitTitle}
        </button>
        <button type="button" onClick={handleClose}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
};

export default CreateOrEditModal;
